import calculateBoundingBox from '@turf/bbox';
import { Feature, FeatureCollection, Point } from 'geojson';
import mapboxgl from 'mapbox-gl';
import type { Map as MapboxMap } from 'mapbox-gl';
import GeojsonMapController from '../../../js/map/geojson_map_controller';
import type { MapLayerSpecification } from '../../../js/map/map_layer_specification';

mapboxgl.accessToken = process.env.MAPBOX_ACCESS_TOKEN ?? '';

const LAND = '#f2e8d0';
const WATER = '#a6d4ea';
const FOREST = '#a9c48a';
const TRAIL = '#e0682b';
const CASING = '#fbf5e6';
const WALKED = '#3b7a2a';
const DEFAULT_STYLE = 'mapbox://styles/mapbox/light-v11';

type ThumbnailFeature = Feature<
  Point,
  { thumbnail?: string; variant: string; stepStatus?: string }
>;

export default class MapController extends GeojsonMapController {
  public static readonly targets = ['canvas'];
  public static readonly values = {
    ...GeojsonMapController.values,
    geoJsonUrl: String,
    style: { type: String, default: DEFAULT_STYLE }
  };

  declare public readonly canvasTarget: HTMLElement;
  declare public readonly styleValue: string;

  protected get _mapContainer(): HTMLElement {
    return this.canvasTarget;
  }

  protected _mapOptions(): Partial<mapboxgl.MapboxOptions> {
    return {
      style: this.styleValue,
      center: [174.8, -41.3],
      zoom: 12,
      minZoom: 10,
      maxZoom: 17,
      attributionControl: false,
      pitch: 0,
      bearing: 0
    };
  }

  protected _registerPulsingDot(): boolean {
    return false;
  }

  protected _bindProgressUpdateClick(): boolean {
    return false;
  }

  protected _beforeSetupMap(): void {
    if (this.styleValue === DEFAULT_STYLE) {
      this._recolorBaseMap();
    }
  }

  private _recolorBaseMap(): void {
    this.map.getStyle().layers.forEach(layer => {
      const id = layer.id.toLowerCase();

      if (layer.type === 'background') {
        this.map.setPaintProperty(layer.id, 'background-color', LAND);

        return;
      }

      if (layer.type !== 'fill') {
        return;
      }

      if (id.includes('water')) {
        this.map.setPaintProperty(layer.id, 'fill-color', WATER);

        return;
      }

      if (
        id.includes('park') ||
        id.includes('landcover') ||
        id.includes('wood') ||
        id.includes('forest')
      ) {
        this.map.setPaintProperty(layer.id, 'fill-color', FOREST);
        this.map.setPaintProperty(layer.id, 'fill-opacity', 0.6);

        return;
      }

      if (id.includes('land')) {
        this.map.setPaintProperty(layer.id, 'fill-color', LAND);
      }
    });
  }

  protected _lineLayers(): MapLayerSpecification[] {
    return [
      {
        id: 'route-casing',
        source: 'map-data',
        type: 'line',
        filter: [
          'in',
          ['get', 'variant'],
          ['literal', ['remaining-path', 'completed-path', 'route-path']]
        ],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': CASING,
          'line-width': ['interpolate', ['linear'], ['zoom'], 12, 8, 17, 20]
        }
      },
      {
        id: 'route',
        source: 'map-data',
        type: 'line',
        filter: [
          'in',
          ['get', 'variant'],
          ['literal', ['remaining-path', 'route-path']]
        ],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': TRAIL,
          'line-width': ['interpolate', ['linear'], ['zoom'], 12, 4, 17, 11]
        }
      },
      {
        id: 'route-walked',
        source: 'map-data',
        type: 'line',
        filter: ['==', ['get', 'variant'], 'completed-path'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': WALKED,
          'line-width': ['interpolate', ['linear'], ['zoom'], 12, 4, 17, 11]
        }
      }
    ];
  }

  protected _handlePointFeatures(mapData: FeatureCollection): void {
    const current = mapData.features.find(
      feature =>
        feature.properties?.variant === 'current-location' &&
        feature.geometry.type === 'Point'
    ) as ThumbnailFeature | undefined;

    for (const feature of mapData.features.filter(
      f =>
        (f.properties?.variant === 'achievement' ||
          f.properties?.variant === 'remaining-achievement') &&
        f.geometry.type === 'Point'
    ) as ThumbnailFeature[]) {
      this._addWildStepsPin(feature, this._pinStatus(feature));
    }

    if (current) {
      this._addYouMarker(current);
    }
  }

  private _pinStatus(feature: ThumbnailFeature): 'found' | 'ahead' {
    const stepStatus = feature.properties.stepStatus;

    if (stepStatus === 'found' || stepStatus === 'ahead') {
      return stepStatus;
    }

    if (feature.properties.variant === 'achievement') {
      return 'found';
    }

    return 'ahead';
  }

  private _addWildStepsPin(
    feature: ThumbnailFeature,
    status: 'found' | 'ahead'
  ): void {
    const pin = document.createElement('span');

    pin.setAttribute('role', 'img');
    pin.className =
      'rounded-circle d-grid overflow-hidden border border-3 shadow';
    pin.style.cssText = 'width: 2.5rem; height: 2.5rem;';

    switch (status) {
      case 'found':
        pin.classList.add('bg-success', 'border-light');
        pin.setAttribute('aria-label', 'Wild Step found');
        break;
      case 'ahead':
        pin.classList.add('bg-body', 'border-secondary');
        pin.setAttribute('aria-label', 'Wild Step not found yet');
        break;
      default: {
        const exhaustive: never = status;

        throw new Error(`Unknown pin status: ${String(exhaustive)}`);
      }
    }

    if (feature.properties.thumbnail) {
      const img = document.createElement('img');

      img.src = feature.properties.thumbnail;
      img.alt = '';
      img.className = 'w-100 h-100 object-fit-cover';
      pin.appendChild(img);
    }

    const marker = new mapboxgl.Marker({ element: pin, anchor: 'center' })
      .setLngLat(feature.geometry.coordinates as [number, number])
      .addTo(this.map);

    this.markers.push(marker);
  }

  private _addYouMarker(feature: ThumbnailFeature): void {
    const you = document.createElement('span');

    you.className = 'rounded-circle bg-ocean border border-3 border-light';
    you.style.cssText = 'width: 1.25rem; height: 1.25rem;';
    you.setAttribute('role', 'img');
    you.setAttribute('aria-label', 'You are here');

    const marker = new mapboxgl.Marker({ element: you, anchor: 'center' })
      .setLngLat(feature.geometry.coordinates as [number, number])
      .addTo(this.map);

    this.markers.push(marker);
  }

  protected _afterSetupMap(mapData: FeatureCollection): void {
    this._fitRoute(this.map, mapData);
    this.canvasTarget.classList.add('opacity-100');
    this.map.resize();
  }

  private _fitRoute(map: MapboxMap, collection: FeatureCollection): void {
    const lines = collection.features.filter(
      feature =>
        feature.geometry.type === 'LineString' ||
        feature.geometry.type === 'MultiLineString'
    );

    if (lines.length === 0) {
      return;
    }

    const bounds = calculateBoundingBox({
      type: 'FeatureCollection',
      features: lines
    });

    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]]
      ],
      { padding: 40, duration: 0, maxZoom: 15 }
    );
  }
}
